export const DEFAULT_TIME_ZONE = "UTC";

export interface TemporalContext {
   currentDateTimeUtc: string;
   timeZone: string;
   currentLocalDateTime: string;
   currentLocalDate: string;
}

const LOCAL_DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
   year: "numeric",
   month: "2-digit",
   day: "2-digit",
   hour: "2-digit",
   minute: "2-digit",
   second: "2-digit",
   hourCycle: "h23"
};

function readDatePart(
   parts: Intl.DateTimeFormatPart[],
   type: Intl.DateTimeFormatPartTypes
): string {
   return parts.find((part) => part.type === type)?.value || "";
}

function formatLocalDateTime(date: Date, timeZone: string): string {
   const parts = new Intl.DateTimeFormat("en-US", {
      ...LOCAL_DATE_TIME_FORMAT_OPTIONS,
      timeZone
   }).formatToParts(date);
   const year = readDatePart(parts, "year");
   const month = readDatePart(parts, "month");
   const day = readDatePart(parts, "day");
   const hour = readDatePart(parts, "hour");
   const minute = readDatePart(parts, "minute");
   const second = readDatePart(parts, "second");

   return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

export function normalizeTimeZone(value: unknown): string {
   if (typeof value !== "string" || !value.trim()) {
      return DEFAULT_TIME_ZONE;
   }

   const candidate = value.trim();
   try {
      new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(0);
      return candidate;
   } catch {
      return DEFAULT_TIME_ZONE;
   }
}

export function getBrowserTimeZone(): string {
   try {
      return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
   } catch {
      return DEFAULT_TIME_ZONE;
   }
}

export function createTemporalContext(
   timeZoneValue?: unknown,
   currentDate: Date = new Date()
): TemporalContext {
   if (Number.isNaN(currentDate.getTime())) {
      throw new Error("Invalid temporal context date");
   }

   const timeZone = normalizeTimeZone(timeZoneValue);
   const currentLocalDateTime = formatLocalDateTime(currentDate, timeZone);

   return {
      currentDateTimeUtc: currentDate.toISOString(),
      timeZone,
      currentLocalDateTime,
      currentLocalDate: currentLocalDateTime.slice(0, 10)
   };
}

export function buildTemporalPromptSection(temporalContext?: TemporalContext): string {
   if (!temporalContext) return "";

   return (
      `## CURRENT TEMPORAL CONTEXT\n` +
      `- Current datetime UTC: ${temporalContext.currentDateTimeUtc}\n` +
      `- User timezone: ${temporalContext.timeZone}\n` +
      `- Current local datetime: ${temporalContext.currentLocalDateTime}\n` +
      `- Current local date: ${temporalContext.currentLocalDate}\n\n` +
      `Use this trusted server time to interpret relative dates and time-sensitive historical facts. ` +
      `If the available facts are insufficient for an exact calculation, ask for clarification when accuracy affects the answer; otherwise state that the result is approximate.`
   );
}
